import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";

export default function AccountSwitcher({ showTradingContext = true }: { showTradingContext?: boolean }) {
  const {
    me, currentAccountId, setCurrentAccountId, currentAccount,
    tradingEnvironment, setTradingEnvironment, environmentAccounts, logout,
  } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!me) return null;
  const isPaper = tradingEnvironment === "paper";
  const label = currentAccount?.name ?? (isPaper ? "尚无模拟账户" : "尚无实盘账户");

  function switchEnvironment(next: "paper" | "live") {
    if (next === tradingEnvironment) return;
    if (next === "live" && !window.confirm("切换到实盘环境？切换后持仓、订单、策略和 Copilot 操作都将指向真实账户。")) return;
    setTradingEnvironment(next);
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 text-xs px-2 py-1 rounded border font-medium ${!showTradingContext ? "border-zinc-300 text-zinc-700 dark:border-zinc-700 dark:text-zinc-200" : isPaper
          ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
          : "border-red-400 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"}`}
        title={showTradingContext ? `${me.username} · ${label}` : me.username}
      >
        {showTradingContext && <><span>{isPaper ? "🟢 模拟" : "🔴 实盘"}</span><span className="opacity-50">｜</span></>}
        <span className="font-medium">{showTradingContext ? label : me.display_name || me.username}</span>
        <span className="text-zinc-400 text-[10px]">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-72 rounded border bg-white dark:bg-zinc-900 shadow-md z-20 text-sm">
          <div className="px-3 py-2 text-xs text-zinc-500 border-b">
            {me.display_name || me.username}（{me.role}）
          </div>
          {showTradingContext && <><div className="grid grid-cols-2 gap-1 border-b p-2">
            <button type="button" onClick={() => switchEnvironment("paper")}
              className={`rounded px-2 py-2 text-xs ${isPaper ? "bg-emerald-600 text-white" : "bg-zinc-100 dark:bg-zinc-800"}`}>
              🟢 模拟环境
            </button>
            <button type="button" onClick={() => switchEnvironment("live")}
              className={`rounded px-2 py-2 text-xs ${!isPaper ? "bg-red-600 text-white" : "bg-zinc-100 dark:bg-zinc-800"}`}>
              🔴 实盘环境
            </button>
          </div>
          <div className="px-3 py-2 text-xs text-zinc-500">
            当前交易区使用{isPaper ? "模拟" : "实盘"}环境，账户操作作用于下方选中的账户
          </div>
          <div className="py-1 border-t">
            {environmentAccounts.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => {
                  setCurrentAccountId(a.id);
                  setOpen(false);
                }}
                className={`block w-full text-left px-3 py-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
                  currentAccountId === a.id ? "font-semibold" : ""
                }`}
              >
                {a.name}
                {a.is_default && <span className="text-[10px] text-zinc-400 ml-1">默认</span>}
                {a.broker && <span className="text-[10px] text-zinc-400 ml-1">{a.broker}</span>}
              </button>
            ))}
            {environmentAccounts.length === 0 && <div className="px-3 py-3 text-xs text-amber-600">
              当前环境没有可用账户，请先创建或连接账户。
            </div>}
          </div>
          </>}
          <div className="border-t py-1">
            {!showTradingContext && <Link to="/quant-agents" onClick={() => setOpen(false)} className="block px-3 py-1.5 text-xs text-blue-600">进入交易区选择账户 →</Link>}
            <Link
              to="/settings/accounts"
              onClick={() => setOpen(false)}
              className="block px-3 py-1.5 text-xs text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              账户管理…
            </Link>
            <button
              type="button"
              onClick={async () => {
                setOpen(false);
                await logout();
                navigate("/login", { replace: true });
              }}
              className="block w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
            >
              退出登录
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
